import React, { useEffect, useState } from 'react';
import { useAlert } from 'react-alert';
import { useTranslation } from 'react-i18next';
import { useLoading } from '../../commons/Loading';
import { useAuth } from '../../commons/Auth';
import {
  BtnModalTable,
  BtnOutlineGreen,
  BtnOutlineRed,
  H4Styled,
} from '../../styled-components';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
} from 'reactstrap';
import InvoiceTabel from './InvoiceTabel';
import ContractsTabel from './ContractsTabel';
import classnames from 'classnames';
import { createBonus, createBonusCommercial } from '../../lib/fetch/reports';
import moment from 'moment';
import { useDebouncedCallback } from 'use-debounce';
import {
  getContractsForIafsNetworkForAll,
  getInsurancesForIafsNetworkForAll,
  getInvoicesForIafsNetworkForAll,
} from '../../lib/fetch/networkForIafsByMonth';
import InsurancesTabel from './InsurancesTabel';

function ModalAllIafs({ data, year, fetchIafsByMonthNetwork }) {
  const [t] = useTranslation('IAF_BY_MONTH');
  const alert = useAlert();
  const { showLoading, hideLoading } = useLoading();
  const [{ newToken, profile = {} } = {}] = useAuth();

  const [modal, setModal] = useState(false);
  const [sentinel, setSentinel] = useState('invoices');

  const [dataForIafsInvoices, setDataForIafsInvoices] = useState([]);
  const [dataForIafsContracts, setDataForIafsContracts] = useState([]);
  const [dataForIafsInsurances, setDataForIafsInsurances] = useState([]);

  const [totIafs, setTotIafs] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [totInvoices, setTotInvoices] = useState(0);
  const [totContracts, setTotContracts] = useState(0);
  const [totInsurances, setTotInsurances] = useState(0);

  const toggle = () => {
    if (!modal) {
      fetchServicesForIaf();
    }
    setModal(!modal);
  };

  const confirmPreview = async (bonus) => {
    if (!(Number(bonus?.amount) > 0)) {
      alert.error(t('COMMON:ERROR_TOTAL_TO_INVOICE_0'));
      return;
    }

    const { error } = await createBonusCommercial({
      newToken,
      bonus: { ...bonus, profileId: data.profileId },
      year,
    });

    if (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }
  };

  const handleConfirm = async () => {
    try {
      showLoading();
      const skippedCategories = [];

      if (dataForIafsInvoices.length > 0 && Number(totInvoices) > 0) {
        await confirmPreview({
          iafType: 'INVOICES',
          amount: totInvoices,
          date: moment.utc().startOf('day').format(),
          accountability: year,
          invoices: dataForIafsInvoices.map(
            ({
              _id,
              accountability,
              admin,
              earnedCommercialShare,
              commercialShare,
              net,
              supplier,
              customer,
              adminQuery,
              date,
              commercialSharePerc,
              number,
            }) => ({
              _id,
              accountability: accountability || undefined,
              admin,
              earnedCommercialShare,
              commercialShare,
              net,
              supplier,
              customer,
              adminQuery,
              date,
              commercialSharePerc,
              number,
            })
          ),
        });
      } else if (dataForIafsInvoices.length > 0) {
        skippedCategories.push(
          `${t('NETWORK_INVOICES_CATEGORY')} (€ ${Number(totInvoices).toFixed(
            2
          )})`
        );
      }

      if (dataForIafsContracts.length > 0 && Number(totContracts) > 0) {
        await confirmPreview({
          iafType: 'ENERGY',
          amount: totContracts,
          date: moment.utc().startOf('day').format(),
          accountability: year,
          contracts: dataForIafsContracts.map(
            ({
              _id,
              admin,
              condo,
              earnedCommercialShare,
              commercialShare,
              net,
              date,
              number,
              contract_signing_date,
            }) => ({
              _id,
              admin,
              condo,
              earnedCommercialShare,
              commercialShare,
              net,
              date,
              number,
              contract_signing_date: new Date(contract_signing_date),
            })
          ),
        });
      } else if (dataForIafsContracts.length > 0) {
        skippedCategories.push(
          `${t('NETWORK_ENERGY_CATEGORY')} (€ ${Number(totContracts).toFixed(
            2
          )})`
        );
      }

      if (dataForIafsInsurances.length > 0 && Number(totInsurances) > 0) {
        await confirmPreview({
          iafType: 'INSURANCES',
          amount: totInsurances,
          date: moment.utc().startOf('day').format(),
          accountability: year,
          insurances: dataForIafsInsurances.map(
            ({
              _id,
              branch,
              policy_code,
              insurance_code,
              start_date,
              end_date,
              gross,
              net,
              shareToInvoice,
              shareToInvoicePerc,
              subAgencyPerc,
              panoramicaShare,
              customerId,
              admin,
              earnedCommercialShare,
              commercialShare,
              agencyType,
            }) => ({
              _id,
              branch,
              policy_code,
              insurance_code,
              start_date,
              end_date,
              gross,
              net,
              shareToInvoice,
              shareToInvoicePerc,
              subAgencyPerc,
              panoramicaShare,
              customerId,
              admin,
              earnedCommercialShare,
              commercialShare,
              agencyType,
            })
          ),
        });
      } else if (dataForIafsInsurances.length > 0) {
        skippedCategories.push(
          `${t('NETWORK_INSURANCES_CATEGORY')} (€ ${Number(
            totInsurances
          ).toFixed(2)})`
        );
      }

      setDataForIafsInvoices([]);
      setDataForIafsContracts([]);
      setDataForIafsInsurances([]);
      toggle();
      await fetchIafsByMonthNetwork(false);

      alert.success(t('COMMON:SUCCESS_UPDATE'));
      if (skippedCategories.length > 0) {
        alert.info(
          t('NETWORK_SKIPPED_NON_POSITIVE', {
            categories: skippedCategories.join(', '),
          })
        );
      }
    } catch (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
    } finally {
      hideLoading();
    }
  };

  const [fetchServicesForIaf] = useDebouncedCallback(async () => {
    showLoading();
    try {
      const [invoicesResponse, contractsResponse, insurancesResponse] =
        await Promise.allSettled([
          getInvoicesForIafsNetworkForAll({
            newToken,
            year,
            commercialId: data.profileId,
          }),
          getContractsForIafsNetworkForAll({
            newToken,
            year,
            commercialId: data.profileId,
          }),
          getInsurancesForIafsNetworkForAll({
            newToken,
            year,
            commercialId: data.profileId,
          }),
        ]);
      setDataForIafsInvoices(
        invoicesResponse.status === 'fulfilled'
          ? invoicesResponse.value?.data?.categories?.variableRevenues?.data ||
              []
          : []
      );
      setDataForIafsContracts(
        contractsResponse.status === 'fulfilled'
          ? contractsResponse.value?.data?.categories?.variableRevenuesEnergy
              ?.data || []
          : []
      );
      setDataForIafsInsurances(
        insurancesResponse.status === 'fulfilled'
          ? insurancesResponse.value?.data?.categories
              ?.variableRevenuesInsurance?.data || []
          : []
      );
      hideLoading();
    } catch (error) {
      hideLoading();
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }
  }, 1000);

  useEffect(() => {
    const totals = (data.months || []).reduce(
      (acc, month) => ({
        invoices: acc.invoices + Number(month.invoicesTotal || 0),
        contracts: acc.contracts + Number(month.contractsTotal || 0),
        insurances: acc.insurances + Number(month.insurancesTotal || 0),
        count: acc.count + Number(month.count || 0),
      }),
      { invoices: 0, contracts: 0, insurances: 0, count: 0 }
    );

    setTotInvoices(totals.invoices);
    setTotContracts(totals.contracts);
    setTotInsurances(totals.insurances);
    setTotalCount(totals.count);
    setTotIafs(
      Number(
        (totals.invoices + totals.contracts + totals.insurances).toFixed(2)
      )
    );
  }, [data]);

  return (
    <>
      <div className="flex-check">
        <BtnModalTable
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            toggle();
          }}
        >
          Emetti tutto
        </BtnModalTable>
      </div>

      <Modal isOpen={modal} className="iafs-modal">
        <ModalHeader toggle={toggle}>
          Anteprima Iafs
          <br />
        </ModalHeader>
        <ModalBody>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <h5 style={{ fontFamily: "'Open Sans', sans-serif" }}>
              Totale da emettere: € {totIafs}
            </h5>
            <h5 style={{ fontFamily: "'Open Sans', sans-serif" }}>
              Elementi da emettere: {totalCount}
            </h5>
          </div>

          <Nav tabs>
            <NavItem>
              <NavLink
                className={classnames({ active: sentinel === 'invoices' })}
                onClick={() => setSentinel('invoices')}
                style={{ cursor: 'pointer' }}
              >
                Fatture
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: sentinel === 'contracts' })}
                onClick={() => setSentinel('contracts')}
                style={{ cursor: 'pointer' }}
              >
                Energia
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({
                  active: sentinel === 'insurances',
                })}
                onClick={() => setSentinel('insurances')}
                style={{ cursor: 'pointer' }}
              >
                Assicurazioni
              </NavLink>
            </NavItem>
          </Nav>

          {sentinel === 'invoices' && dataForIafsInvoices?.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <InvoiceTabel
                data={dataForIafsInvoices}
                onFilter={() => {}}
                query={{
                  limit: dataForIafsInvoices?.length || 50,
                  offset: 0,
                }}
                total={dataForIafsInvoices?.length}
              />
            </div>
          )}

          {sentinel === 'invoices' && dataForIafsInvoices?.length === 0 && (
            <H4Styled style={{ textAlign: 'center', marginTop: '15px' }}>
              Non ci sono fatture su cui emettere iaf
            </H4Styled>
          )}

          {sentinel === 'contracts' && dataForIafsContracts?.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <ContractsTabel
                data={dataForIafsContracts}
                onFilter={() => {}}
                query={{
                  limit: dataForIafsContracts?.length || 50,
                  offset: 0,
                }}
                total={dataForIafsContracts?.length}
              />
            </div>
          )}

          {sentinel === 'contracts' && dataForIafsContracts?.length === 0 && (
            <H4Styled style={{ textAlign: 'center', marginTop: '15px' }}>
              Non ci sono contratti energia su cui emettere iaf
            </H4Styled>
          )}

          {sentinel === 'insurances' && dataForIafsInsurances?.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <InsurancesTabel
                data={dataForIafsInsurances}
                onFilter={() => {}}
                query={{
                  limit: dataForIafsInsurances?.length || 50,
                  offset: 0,
                }}
                total={dataForIafsInsurances?.length}
              />
            </div>
          )}

          {sentinel === 'insurances' && dataForIafsInsurances?.length === 0 && (
            <H4Styled style={{ textAlign: 'center', marginTop: '15px' }}>
              Non ci sono assicurazioni su cui emettere iaf
            </H4Styled>
          )}
        </ModalBody>
        <ModalFooter>
          <BtnOutlineRed type="button" className="uppercase" onClick={toggle}>
            {t('COMMON:CANCEL')}
          </BtnOutlineRed>
          <BtnOutlineGreen
            type="button"
            className="uppercase"
            onClick={handleConfirm}
          >
            Conferma IAF
          </BtnOutlineGreen>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default ModalAllIafs;
